// react
import { useContext } from 'react';

// types
import { Activity, IlapCategoryRetryable, Status } from '../../../@types';
import { ilapRetryableCategories } from '../../../@types/unions';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextProject } from '../../pages/Project/Project.context';

// mui
import { Box, Grid, Paper, Tooltip, Typography } from '@mui/material';

// ui
import { BtnMenu } from '../../ui/BtnMenu/BtnMenu';
import { ChipStatus } from '../../ui/ChipStatus/ChipStatus';
import { IconCircle } from '../../ui/IconCircle/IconCircle';
import { RowKeyValue } from '../../ui/RowKeyValue/RowKeyValue';
import { UserAvatarModified } from '../../ui/UserAvatarModified/UserAvatarModified';

// utils
import { constructActivityItems } from '../../../utils/activities';

// theme
import { theme } from '../../../theme';

// icons
import {
	AddCircleOutlineOutlined as IconAddCircleOutline,
	ArrowCircleRightOutlined as IconCircleRight,
	FlagOutlined as IconFlag,
	RefreshOutlined as IconRefresh,
	StoreOutlined as IconStore,
} from '@mui/icons-material';

// props
export interface CardActivityProps {
	activity: Activity;
	refetchActivities?: () => void;
}

export const CardActivity = ({ activity, refetchActivities }: CardActivityProps) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);

	// handlers
	const handleRetryIlapJob = async () => {
		if (!activity.ilapJob?.id) return;

		try {
			const accessToken = sessionStorage.getItem('accessToken');
			const idToken = sessionStorage.getItem('idToken');

			const response = await fetch(
				`${process.env.REACT_APP_API_URL}/ilap-jobs/${activity.ilapJob.id}`,
				{
					method: 'PATCH',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
					body: JSON.stringify({ status: 'RETRY' }),
				}
			);

			if (!response.ok) {
				throw new Error('Failed to retry ILAP job');
			}

			// Refetch activities to show new status
			if (refetchActivities) {
				refetchActivities();
			}
		} catch (error) {
			console.error('Error retrying ILAP job:', error);
		}
	};

	// vars
	const activityItems = constructActivityItems({ activities: [activity], t });
	const activityItem = activityItems[0];

	// Menu items
	const btnMenuItems = [];

	// Add contextual link
	if (activityItem?.to) {
		btnMenuItems.push({
			children: t('seeDetails'),
			icon: <IconCircleRight />,
			to: activityItem.to,
		});
	}

	// Add iLAP menu items
	if (activity.ilapJob) {
		const { ilapJob } = activity;
		const { category, message, status } = ilapJob;

		if (status === 'FAILED') {
			const isRetryableError =
				category && ilapRetryableCategories.includes(category as IlapCategoryRetryable);

			if (category === 'ILAP_NO_DA_FOUND') {
				const urlAccounts = `/project/${project?.idWbg}/accounts/local`;
				const accountName = message?.DA?.NAME;
				const institution = message?.BeneficiaryBankDetails?.NAME;
				const routeCode = message?.BeneficiaryBankDetails?.ROUTECODE?.VALUE;
				const swiftCode = message?.BeneficiaryBankDetails?.SWIFTCODE?.VALUE;

				let urlAddAccount = `${urlAccounts}?dialog=addAccount&dialogType=LOCAL`;
				if (accountName) {
					urlAddAccount += `&dialogAccountName=${accountName}`;
				}
				if (institution) {
					urlAddAccount += `&dialogInstitution=${institution}`;
				}
				if (routeCode) {
					urlAddAccount += `&dialogRouteCode=${routeCode}`;
				}
				if (swiftCode) {
					urlAddAccount += `&dialogSwiftCode=${swiftCode}`;
				}

				btnMenuItems.push({
					children: t('seeAccounts'),
					icon: <IconFlag />,
					to: urlAccounts,
				});

				btnMenuItems.push({
					children: t('addAccount'),
					icon: <IconAddCircleOutline />,
					to: urlAddAccount,
				});
			}

			if (category === 'ILAP_NO_SUPPLIER_FOUND') {
				const supplierName = message?.BeneficiaryDetails?.NAME;
				const urlSuppliers = `/project/${project?.idWbg}/suppliers`;

				let urlAddSupplier = `${urlSuppliers}?dialog=addSupplier`;
				if (supplierName) {
					urlAddSupplier += `&dialogSupplierName=${supplierName}`;
				}

				btnMenuItems.push({
					children: t('seeSuppliers'),
					icon: <IconStore />,
					to: urlSuppliers,
				});

				btnMenuItems.push({
					children: t('addSupplier'),
					icon: <IconAddCircleOutline />,
					to: urlAddSupplier,
				});
			}

			if (isRetryableError) {
				btnMenuItems.push({
					children: t('retry'),
					icon: <IconRefresh />,
					onClick: handleRetryIlapJob,
				});
			}
		}
	}

	return (
		<Paper>
			<Box
				sx={(theme) => ({
					position: 'relative',
					padding: theme.spacing(2),
				})}>
				<Grid alignItems="center" container spacing={2}>
					{/* Activity Type/Content */}
					<Grid item xs={12} lg={5}>
						<Grid alignItems="center" container spacing={2}>
							<Grid item>
								<IconCircle type={activityItem?.type || 'other'} />
							</Grid>
							<Grid flex={1} item>
								{activityItem?.title && (
									<Typography variant="h3" fontWeight={600}>
										{activityItem.title}
									</Typography>
								)}
								{activity.ilapJob?.errorMessage && activityItem?.content ? (
									<Tooltip title={activity.ilapJob.errorMessage} arrow placement="top">
										<Typography variant="body2" sx={{ cursor: 'help' }}>
											{activityItem.content}
										</Typography>
									</Tooltip>
								) : (
									<Typography variant="body2">
										{activityItem?.content || <Typography>{t('activities.unknown')}</Typography>}
									</Typography>
								)}
							</Grid>
						</Grid>
					</Grid>{' '}
					{/* Type */}
					<Grid item xs={12} lg={1.5}>
						<RowKeyValue label={t('source')} value={activity.ilapJob ? 'ILAP' : 'FundsChain'} />
					</Grid>
					{/* Status */}
					<Grid item xs={12} lg={1.5}>
						<RowKeyValue
							label={t('status')}
							value={
								activity.ilapJob?.status ? (
									<ChipStatus
										status={
											activity.ilapJob.status === 'RETRY'
												? 'PENDING'
												: (activity.ilapJob.status.toUpperCase() as Status)
										}
									/>
								) : (
									<ChipStatus status="COMPLETED" />
								)
							}
						/>
					</Grid>
					{/* Created Date & User */}
					<Grid item xs={12} lg={3}>
						<RowKeyValue label={t('created')} value={<UserAvatarModified activity={activity} />} />
					</Grid>
					{/* Actions */}
					{btnMenuItems.length > 0 && (
						<Grid item xs={12} lg={true}>
							<Grid container justifyContent={{ lg: 'flex-end' }} spacing={1}>
								<Grid
									item
									sx={{
										position: 'absolute',
										top: 0,
										right: theme.spacing(1),
										[theme.breakpoints.up('lg')]: {
											position: 'relative',
											top: 'auto',
											right: 'auto',
										},
									}}>
									<BtnMenu items={btnMenuItems} />
								</Grid>
							</Grid>
						</Grid>
					)}
				</Grid>
			</Box>
		</Paper>
	);
};
